import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import ImageBase64Tool from '../components/ImageBase64Tool';
import FaqSection from '../components/Faq';
import { PageHero } from '../components/InfoSections';
import { useTheme } from '../theme';
import { softwareApplicationSchema, faqPageSchema, breadcrumbSchema, Faq } from '../lib/schema';

const FAQS: Faq[] = [
  { q: 'How do I convert Base64 back to an image?', a: 'Paste a Base64 string or a full data:image/... URI above and the image previews instantly. Click Download to save it as a file.' },
  { q: 'My Base64 string has no data: prefix — will it work?', a: 'Yes. If you paste raw Base64 without a data URI prefix, the tool assumes PNG and renders it. For other formats, include the proper data:image/<type>;base64, prefix.' },
  { q: 'Why does my image not show?', a: 'The string may be truncated, not actually image data, or use a different format than assumed. Make sure you copied the entire Base64 value.' },
  { q: 'Is the image uploaded anywhere?', a: 'No. Decoding happens entirely in your browser; nothing is sent to a server.' },
];

export default function Base64ToImagePage() {
  const { themeClasses } = useTheme();
  return (
    <>
      <Seo
        title="Base64 to Image Converter — Decode & Download | EasyBase64"
        description="Convert a Base64 string or data URI back into an image online. Preview and download the decoded PNG/JPG instantly. Free and fully private — no upload."
        path="/base64-to-image"
        jsonLd={[
          softwareApplicationSchema({ name: 'Base64 to Image Converter', description: 'Decode Base64 strings into viewable, downloadable images in the browser.', path: '/base64-to-image' }),
          faqPageSchema(FAQS),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Base64 to Image', path: '/base64-to-image' }]),
        ]}
      />
      <PageHero
        title="Base64 to Image"
        subtitle={<>Decode a Base64 string or data URI back into a viewable, downloadable image. Need to go the other way? Use <Link to="/image-to-base64" className="underline">Image to Base64</Link>.</>}
      />
      <div className="mb-12">
        <ImageBase64Tool direction="decode" />
      </div>
      <section className={`${themeClasses.infoCard} rounded-2xl p-5 sm:p-8 border mb-12 max-w-3xl mx-auto`}>
        <h2 className={`text-2xl font-bold mb-4 ${themeClasses.textPrimary}`}>Accepted input</h2>
        <p className={`${themeClasses.textSecondary} leading-relaxed`}>
          Paste either a full data URI (<code>data:image/png;base64,iVBORw0K...</code>) or just the raw Base64 payload.
          With a data URI the exact MIME type is preserved; with raw Base64 the tool assumes PNG for the preview and download.
        </p>
      </section>
      <FaqSection title="Base64 to Image — FAQ" faqs={FAQS} />
    </>
  );
}
